class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    this.initEvents();
  }

  initEvents() {
    // Close button is now inside the content, handle via event delegation
    document.addEventListener("click", (e) => {
      if (e.target.closest(".quick-add-header__close")) {
        this.close();
      }
    });

    // Close on overlay click (clicking outside modal content)
    const overlay = this.modal.querySelector(".quick-add-modal__overlay");
    if (overlay) {
      overlay.addEventListener("click", () => {
        this.close();
      });
    }

    // Handle ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modal.classList.contains("open")) {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });

    // Handle quick-add buttons - both direct add and modal
    // Use a shared handler function to avoid duplication
    const handleQuickAdd = (e) => {
      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (!quickAddBtn) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const productHandle = quickAddBtn.dataset.productHandle;
      const hasVariants = quickAddBtn.dataset.hasVariants === "true";
      const variantId = quickAddBtn.dataset.variantId;

      if (!productHandle) return;

      // Direct add to cart for single variant products
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, quickAddBtn);
      } else {
        // Open modal for products with variants (loading shown in open method)
        this.open(productHandle);
      }
    };

    // Handle click events (desktop) - use capture phase to get priority
    document.addEventListener("click", handleQuickAdd, { capture: true });

    // Handle touch events (mobile) - use capture phase and direct button listeners
    let touchStartTime = 0;
    let touchStartTarget = null;
    let touchStartX = 0;
    let touchStartY = 0;

    // Use capture phase to intercept before sliders
    document.addEventListener(
      "touchstart",
      (e) => {
        const quickAddBtn = e.target.closest(".quick-add-btn");
        if (quickAddBtn) {
          // Stop propagation immediately so sliders don't interfere
          e.stopPropagation();
          touchStartTime = Date.now();
          touchStartTarget = quickAddBtn;
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }
      },
      { passive: true, capture: true }
    );

    document.addEventListener(
      "touchend",
      (e) => {
        const quickAddBtn = e.target.closest(".quick-add-btn");
        if (!quickAddBtn || quickAddBtn !== touchStartTarget) {
          touchStartTarget = null;
          return;
        }

        // Calculate movement distance
        const touchEndX = e.changedTouches[0].clientX;
        const touchEndY = e.changedTouches[0].clientY;
        const movementDistance = Math.sqrt(
          Math.pow(touchEndX - touchStartX, 2) +
            Math.pow(touchEndY - touchStartY, 2)
        );

        // Only trigger if it was a quick tap (less than 300ms) and minimal movement (less than 10px)
        const touchDuration = Date.now() - touchStartTime;
        if (touchDuration < 300 && movementDistance < 10) {
          // Stop propagation to prevent slider interference
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          handleQuickAdd(e);
        }

        touchStartTarget = null;
      },
      { passive: false, capture: true }
    );
  }

  open(productHandle) {
    if (
      this.currentProductHandle === productHandle &&
      this.modal.classList.contains("open")
    ) {
      return;
    }

    this.currentProductHandle = productHandle;

    // Show loading in button first
    const quickAddBtn = document.querySelector(
      `.quick-add-btn[data-product-handle="${productHandle}"]`
    );
    if (quickAddBtn) {
      this.showButtonLoading(quickAddBtn);
    }

    // Open modal drawer
    this.modal.classList.add("open");
    document.body.style.overflow = "hidden";

    // Check if cart drawer is open
    const cartDrawer = document.getElementById("CartDrawer");
    const isCartOpen = cartDrawer && cartDrawer.classList.contains("open");

    // Set overlay z-index above search popup, below modal
    const mainOverlay = document.querySelector(".main__overlay");
    if (mainOverlay && window.setOverlayZIndex && window.Z_INDEX) {
      mainOverlay.classList.add("active");
      // If cart is open, set overlay above cart drawer
      if (isCartOpen) {
        window.setOverlayZIndex(window.Z_INDEX.OVERLAY_QUICK_ADD_ABOVE_CART);
      } else {
        window.setOverlayZIndex(window.Z_INDEX.OVERLAY_QUICK_ADD);
      }
    }

    // Set active component
    if (window.activeComponent !== undefined) {
      window.activeComponent = "quick-add";
    }

    this.fetchProduct(productHandle);
  }

  close() {
    this.modal.classList.remove("open");
    this.currentProductHandle = null;
    this.currentProductData = null;

    // Check what's still open and manage overlay accordingly
    const mainOverlay = document.querySelector(".main__overlay");
    const cartDrawer = document.getElementById("CartDrawer");
    const isCartOpen = cartDrawer && cartDrawer.classList.contains("open");

    // Check if search popup is still open
    const searchPopup = document.querySelector(".header__search-popup");
    const isSearchOpen =
      searchPopup && searchPopup.classList.contains("active");

    if (mainOverlay && window.setOverlayZIndex && window.Z_INDEX) {
      if (isCartOpen) {
        // Cart drawer is still open, set overlay above modal but below drawer
        mainOverlay.classList.add("active");
        window.setOverlayZIndex(window.Z_INDEX.OVERLAY_CART_DRAWER);
      } else if (isSearchOpen) {
        // Search is still open, reset overlay to default (below search)
        mainOverlay.classList.add("active");
        window.resetOverlayZIndex();
      } else {
        // No other popup open, remove overlay
        mainOverlay.classList.remove("active");
        window.resetOverlayZIndex();
      }
    }

    // Reset active component
    if (window.activeComponent === "quick-add") {
      if (isCartOpen) {
        window.activeComponent = "cart";
      } else {
        window.activeComponent = null;
      }
    }

    // Only reset body overflow if cart drawer is not open
    if (!isCartOpen) {
      document.body.style.overflow = "";
    }
  }

  async addToCartDirect(variantId, button) {
    const originalHTML =
      button.querySelector(".quick-add-btn-label")?.innerHTML ||
      button.innerHTML;
    const labelElement = button.querySelector(".quick-add-btn-label");

    // Store original HTML for restoration
    button.dataset.originalHTML = originalHTML;

    // Show loading dots instead of "Adding..." text
    this.showButtonLoading(button);

    try {
      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Publish cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          productId: button.dataset.productId,
        });

        // Hide loading dots first
        this.restoreButtonText(button);

        // Check if this is an upsell slider button - skip "Added!" state for these
        const isUpsellButton = button.classList.contains(
          "product-upsell-quick-add-btn"
        );

        if (isUpsellButton) {
          // For upsell buttons, just restore immediately without showing "Added!"
          button.disabled = false;
          if (labelElement) {
            labelElement.innerHTML = originalHTML;
          } else {
            button.innerHTML = originalHTML;
          }
          delete button.dataset.originalHTML;
        } else {
          // Show success state for regular buttons
          if (labelElement) {
            labelElement.textContent = "Added!";
          } else {
            button.textContent = "Added!";
          }

          // Reset after delay
          setTimeout(() => {
            button.disabled = false;
            if (labelElement) {
              labelElement.innerHTML = originalHTML;
            } else {
              button.innerHTML = originalHTML;
            }
            delete button.dataset.originalHTML;
          }, 1500);
        }
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Publish error event
      publish(PUB_SUB_EVENTS.cartError, {
        error: error.message,
        context: "quick-add-direct",
        productId: button.dataset.productId,
      });

      // Hide loading dots first
      this.restoreButtonText(button);

      // Restore button text on error
      if (labelElement) {
        labelElement.textContent = "Error";
      } else {
        button.textContent = "Error";
      }

      setTimeout(() => {
        button.disabled = false;
        if (labelElement) {
          labelElement.innerHTML = originalHTML;
        } else {
          button.innerHTML = originalHTML;
        }
        delete button.dataset.originalHTML;
      }, 2000);
    }
  }

  showButtonLoading(button) {
    // Make button relative positioning if not already
    if (getComputedStyle(button).position === "static") {
      button.style.position = "relative";
    }

    // Check if loading dots already exist
    let loadingDots = button.querySelector(".quick-add-loading-dots");

    if (!loadingDots) {
      // Create loading dots HTML as overlay
      loadingDots = document.createElement("div");
      loadingDots.className = "quick-add-loading-dots";
      loadingDots.innerHTML = `
        <span class="dot"></span>
        <span class="dot"></span>
        <span class="dot"></span>
      `;
      button.appendChild(loadingDots);
    }

    // Show loading dots with opacity 1
    loadingDots.style.opacity = "1";
    button.disabled = true;
  }

  restoreButtonText(button) {
    const loadingDots = button.querySelector(".quick-add-loading-dots");
    if (loadingDots) {
      // Fade out loading dots
      loadingDots.style.opacity = "0";
      loadingDots.style.transition = "opacity 0.3s ease";

      // Remove after fade out completes
      setTimeout(() => {
        if (loadingDots.parentNode) {
          loadingDots.remove();
        }
      }, 300);
    }
    button.disabled = false;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button onclick="window.quickAddModal.close()">Close</button>
      </div>
    `;
  }

  showModalLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-modal-loading">
        <div class="quick-add-loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    // Show loading in modal
    this.showModalLoading();

    try {
      const response = await fetch(
        `/products/${productHandle}?section_id=product-quick-add`
      );
      if (!response.ok) throw new Error("Failed to fetch product");

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const productHTML = doc.querySelector(".quick-add-product");

      if (!productHTML) throw new Error("Invalid product content");

      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(productHTML);

      // Store product variants data
      const variantsData = productHTML.dataset.productVariants;
      if (variantsData) {
        this.currentProductData = JSON.parse(variantsData);
      }

      // Restore button text after loading
      const quickAddBtn = document.querySelector(
        `.quick-add-btn[data-product-handle="${this.currentProductHandle}"]`
      );
      if (quickAddBtn) {
        this.restoreButtonText(quickAddBtn);
      }

      this.initProductScripts();

      // Initialize wishlist button state for dynamically loaded content
      if (
        window.wishlist &&
        typeof window.wishlist.setInitialStates === "function"
      ) {
        window.wishlist.setInitialStates(this.modalContent);
      }
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();

      // Restore button text on error
      const quickAddBtn = document.querySelector(
        `.quick-add-btn[data-product-handle="${this.currentProductHandle}"]`
      );
      if (quickAddBtn) {
        this.restoreButtonText(quickAddBtn);
      }
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;

    this.initImageSlider(productContainer);
    this.initImageZoom(productContainer); // Add this line
    this.initVariantSelection(productContainer, productHandle);
    this.initQuantitySelector(productContainer);
    this.initFormSubmission(productContainer);
    this.initDescriptionToggle(productContainer);
  }

  initDescriptionToggle(container) {
    const descriptionEl = container.querySelector(".quick-add-description");
    if (!descriptionEl) return;

    const toggleBtn = descriptionEl.querySelector(
      ".quick-add-description__toggle"
    );
    if (!toggleBtn) return;

    const moreSpan = toggleBtn.querySelector(".quick-add-description__more");
    const lessSpan = toggleBtn.querySelector(".quick-add-description__less");
    const textSpan = descriptionEl.querySelector(
      ".quick-add-description__text"
    );

    const fullDescription = descriptionEl.dataset.fullDescription;
    const truncatedDescription = descriptionEl.dataset.truncatedDescription;

    if (!fullDescription || !truncatedDescription) return;

    toggleBtn.addEventListener("click", () => {
      const isExpanded = descriptionEl.classList.contains("expanded");

      if (isExpanded) {
        // Collapse
        descriptionEl.classList.remove("expanded");
        textSpan.textContent = `${truncatedDescription}...`;
        moreSpan.style.display = "inline";
        lessSpan.style.display = "none";
      } else {
        // Expand
        descriptionEl.classList.add("expanded");
        textSpan.textContent = fullDescription;
        moreSpan.style.display = "none";
        lessSpan.style.display = "inline";
      }
    });
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const counter = container.querySelector(
      ".quick-add-counter .current-slide"
    );

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateCounter = () => {
      if (counter) counter.textContent = currentSlide + 1;
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateCounter();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateCounter();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    // Handle color swatch clicks (like product card)
    const colorSwatches = container.querySelectorAll(".quick-add-color-option");
    colorSwatches.forEach((swatch) => {
      swatch.addEventListener("click", (e) => {
        e.preventDefault();

        // Get the associated radio input
        const optionPosition = swatch.dataset.optionPosition;
        const value = swatch.dataset.value;
        const radio = container.querySelector(
          `input[name="option-${optionPosition}"][value="${CSS.escape(value)}"]`
        );

        if (radio && !radio.disabled) {
          // Update aria-current for all swatches in this color group
          swatch
            .closest(".quick-add-colors")
            ?.querySelectorAll(".quick-add-color-option")
            .forEach((btn) => {
              btn.setAttribute("aria-current", "false");
              btn.classList.remove("selected");
            });
          swatch.setAttribute("aria-current", "true");
          swatch.classList.add("selected");

          // Update selected-value in legend
          const fieldset = swatch.closest(".variant-option");
          if (fieldset) {
            const selectedValueSpan = fieldset.querySelector(
              "legend .selected-value"
            );
            if (selectedValueSpan) {
              selectedValueSpan.textContent = value;
            }
          }

          // Check the radio input
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));

          // Update option availability
          const selectedOptions = this.getSelectedOptions(container);
          this.updateOptionAvailability(container, selectedOptions);

          // Update stock indicators after color change
          const stockDataElement = container.querySelector(
            "#quick-add-variant-stock-data"
          );
          const stockData = stockDataElement
            ? JSON.parse(stockDataElement.textContent)
            : null;
          if (stockData) {
            const variantStockEls = container.querySelectorAll(
              ".variant-option .option-value"
            );
            const colorSwatchEls = container.querySelectorAll(
              ".quick-add-color-option"
            );
            this.updateStockIndicators(
              container,
              stockData,
              variantStockEls,
              selectedOptions
            );
            this.updateStockIndicators(
              container,
              stockData,
              colorSwatchEls,
              selectedOptions
            );
          }

          // Update only the second image (variant image) if color swatch has image data
          const newImageSrc = swatch.dataset.imageSrc;
          const newAltText = swatch.dataset.imageAlt;
          if (newImageSrc) {
            const secondImage = container.querySelector(
              ".quick-add-image img[data-variant-image]"
            );
            if (secondImage && secondImage.src !== newImageSrc) {
              secondImage.src = newImageSrc;
              secondImage.alt = newAltText || secondImage.alt;
            }
          }
        }
      });
    });

    const variantRadios = container.querySelectorAll(
      '.variant-option input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".add-to-cart-btn");
    const variantInput = container.querySelector('input[name="id"]');
    const currentPriceEl = container.querySelector(".current-price");
    const comparePriceEl = container.querySelector(".compare-price");
    const savingsEl = container.querySelector(".savings .save-discount");
    const stockDataElement = container.querySelector(
      "#quick-add-variant-stock-data"
    );
    const stockData = stockDataElement
      ? JSON.parse(stockDataElement.textContent)
      : null;
    const variantStockEls = container.querySelectorAll(
      ".variant-option .option-value"
    );
    const colorSwatchEls = container.querySelectorAll(
      ".quick-add-color-option"
    );

    // Get variant count for button text formatting
    const shopifyVariantCount =
      this.currentProductData && Array.isArray(this.currentProductData)
        ? this.currentProductData.length
        : 0;

    if (variantRadios.length === 0 && colorSwatches.length === 0) return;

    // Initialize color swatch states
    colorSwatches.forEach((swatch) => {
      const optionPosition = swatch.dataset.optionPosition;
      const value = swatch.dataset.value;
      const radio = container.querySelector(
        `input[name="option-${optionPosition}"][value="${CSS.escape(value)}"]`
      );
      if (radio && radio.checked) {
        swatch.setAttribute("aria-current", "true");
        swatch.classList.add("selected");

        // Update selected-value in legend
        const fieldset = swatch.closest(".variant-option");
        if (fieldset) {
          const selectedValueSpan = fieldset.querySelector(
            "legend .selected-value"
          );
          if (selectedValueSpan) {
            selectedValueSpan.textContent = value;
          }
        }
      } else {
        swatch.setAttribute("aria-current", "false");
        swatch.classList.remove("selected");
      }
    });

    // Initialize option availability
    const initialSelectedOptions = this.getSelectedOptions(container);
    this.updateOptionAvailability(container, initialSelectedOptions);

    // Initialize stock indicators for both regular options and color swatches
    if (stockData) {
      this.updateStockIndicators(container, stockData, variantStockEls, []);
      this.updateStockIndicators(container, stockData, colorSwatchEls, []);
    }

    // Set initial button text based on current variant
    if (
      addToCartBtn &&
      this.currentProductData &&
      this.currentProductData.length > 0
    ) {
      const currentVariantId = variantInput
        ? parseInt(variantInput.value)
        : null;
      if (currentVariantId) {
        const currentVariant = this.currentProductData.find(
          (v) => v.id === currentVariantId
        );
        if (currentVariant) {
          if (currentVariant.available) {
            let variantText = currentVariant.options
              ? currentVariant.options.join("/")
              : "";
            const addToCartText =
              window.translations?.cart?.add_to_cart || "Add to Cart";

            if (shopifyVariantCount > 1 && variantText) {
              addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
            } else {
              addToCartBtn.textContent = addToCartText;
            }
            addToCartBtn.disabled = false;
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent =
              window.translations?.cart?.sold_out || "Sold Out";
          }
        }
      }
    }

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = this.getSelectedOptions(container);
        const selectedVariant = this.findVariantByOptions(selectedOptions);

        // Update option availability
        this.updateOptionAvailability(container, selectedOptions);

        if (selectedVariant) {
          if (variantInput) variantInput.value = selectedVariant.id;

          // Update price display (matching main-product format)
          if (currentPriceEl) {
            currentPriceEl.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_without_trailing_zeros_format || window.money_format
            );
          }

          if (comparePriceEl && savingsEl) {
            if (selectedVariant.compare_at_price > selectedVariant.price) {
              comparePriceEl.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_without_trailing_zeros_format ||
                  window.money_format
              );
              comparePriceEl.style.display = "inline";
              const saveValue =
                100 -
                (selectedVariant.price / selectedVariant.compare_at_price) *
                  100;
              savingsEl.textContent = `-${Math.ceil(saveValue)}%`;
              savingsEl.closest(".savings").style.display = "inline";
            } else {
              comparePriceEl.style.display = "none";
              savingsEl.closest(".savings").style.display = "none";
            }
          }

          // Update button (matching main-product format)
          if (addToCartBtn) {
            if (selectedVariant.available) {
              let variantText = selectedVariant.options
                ? selectedVariant.options.join("/")
                : "";
              const addToCartText =
                window.translations?.cart?.add_to_cart || "Add to Cart";

              if (shopifyVariantCount > 1 && variantText) {
                addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
              } else {
                addToCartBtn.textContent = addToCartText;
              }
              addToCartBtn.disabled = false;
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.textContent =
                window.translations?.cart?.sold_out || "Sold Out";
            }
          }

          // Update stock indicators for both regular options and color swatches
          if (stockData) {
            const variantStockEls = container.querySelectorAll(
              ".variant-option .option-value"
            );
            const colorSwatchEls = container.querySelectorAll(
              ".quick-add-color-option"
            );
            this.updateStockIndicators(
              container,
              stockData,
              variantStockEls,
              selectedOptions
            );
            this.updateStockIndicators(
              container,
              stockData,
              colorSwatchEls,
              selectedOptions
            );
          }

          // Update only the second image (variant image) if variant has featured image
          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=400";
            const secondImage = container.querySelector(
              ".quick-add-image img[data-variant-image]"
            );

            if (secondImage) {
              secondImage.src = newImageSrc;
              secondImage.alt = selectedVariant.featured_image.alt || "";
            }
          }
        }
      });
    });
  }

  getSelectedOptions(container) {
    const selectedOptions = [];

    // Get all checked radios (including those from color swatches)
    const allRadios = container.querySelectorAll(
      '.quick-add-variant-selector input[type="radio"]:checked'
    );

    // Build options array based on option position
    const optionsMap = new Map();
    allRadios.forEach((radio) => {
      const optionPosition = parseInt(radio.name.replace("option-", ""));
      optionsMap.set(optionPosition, radio.value);
    });

    // Update color swatch states
    const colorSwatches = container.querySelectorAll(".quick-add-color-option");
    colorSwatches.forEach((swatch) => {
      const optionPosition = swatch.dataset.optionPosition;
      const value = swatch.dataset.value;
      const radio = container.querySelector(
        `input[name="option-${optionPosition}"][value="${CSS.escape(value)}"]`
      );
      if (radio && radio.checked) {
        // Update color swatch state
        swatch
          .closest(".quick-add-colors")
          ?.querySelectorAll(".quick-add-color-option")
          .forEach((btn) => {
            btn.setAttribute("aria-current", "false");
            btn.classList.remove("selected");
          });
        swatch.setAttribute("aria-current", "true");
        swatch.classList.add("selected");
      }
    });

    // Handle regular variant options
    container.querySelectorAll(".variant-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const labels = group.querySelectorAll(".option-value");
      labels.forEach((label) => label.classList.remove("selected"));
      if (checked) {
        const checkedLabel = group.querySelector(`label[for="${checked.id}"]`);
        if (checkedLabel) checkedLabel.classList.add("selected");

        // Update selected-value span in legend
        const selectedValueSpan = group.querySelector("legend .selected-value");
        if (selectedValueSpan) {
          selectedValueSpan.textContent = checked.value;
        }
      }
    });

    // Return options in order (option 1, option 2, option 3, etc.)
    const maxPosition = Math.max(...Array.from(optionsMap.keys()), 0);
    for (let i = 1; i <= maxPosition; i++) {
      selectedOptions.push(optionsMap.get(i) || null);
    }

    return selectedOptions;
  }

  findVariantByOptions(options) {
    if (!this.currentProductData) return null;
    return this.currentProductData.find((variant) => {
      return variant.options.every(
        (opt, idx) => !options[idx] || options[idx] === opt
      );
    });
  }

  updateOptionAvailability(container, selectedOptions) {
    if (!this.currentProductData) return;

    // Get product options from stock data
    const stockDataElement = container.querySelector(
      "#quick-add-variant-stock-data"
    );
    const stockData = stockDataElement
      ? JSON.parse(stockDataElement.textContent)
      : null;
    const productOptions = stockData?.options || [];

    // Update availability for each option group
    productOptions.forEach((optionName, optionIndex) => {
      const fieldsets = container.querySelectorAll(".variant-option");
      const fieldset = fieldsets[optionIndex];
      if (!fieldset) return;

      const groupInputs = fieldset.querySelectorAll('input[type="radio"]');
      const colorSwatches = fieldset.querySelectorAll(
        ".quick-add-color-option"
      );

      groupInputs.forEach((input) => {
        const inputValue = input.value;
        const isPossible = this.currentProductData.some((variant) => {
          if (!variant.available) return false;
          return variant.options.every((opt, idx) => {
            if (idx === optionIndex) return opt === inputValue;
            return !selectedOptions[idx] || selectedOptions[idx] === opt;
          });
        });

        if (isPossible) {
          input.classList.remove("disabled");
        } else {
          input.classList.add("disabled");
        }

        const label = fieldset.querySelector(`label[for="${input.id}"]`);
        if (label) {
          if (isPossible) {
            label.classList.remove("disabled", "sold-out");
          } else {
            label.classList.add("disabled", "sold-out");
          }
        }
      });

      colorSwatches.forEach((swatch) => {
        const swatchValue = swatch.dataset.value;
        if (!swatchValue) return;

        const isPossible = this.currentProductData.some((variant) => {
          if (!variant.available) return false;
          return variant.options.every((opt, idx) => {
            if (idx === optionIndex) return opt === swatchValue;
            return !selectedOptions[idx] || selectedOptions[idx] === opt;
          });
        });

        if (isPossible) {
          swatch.classList.remove("disabled", "sold-out");
          const optionPosition = swatch.dataset.optionPosition;
          const radio = container.querySelector(
            `input[name="option-${optionPosition}"][value="${CSS.escape(
              swatchValue
            )}"]`
          );
          if (radio) {
            radio.classList.remove("disabled");
          }
        } else {
          swatch.classList.add("disabled", "sold-out");
          const optionPosition = swatch.dataset.optionPosition;
          const radio = container.querySelector(
            `input[name="option-${optionPosition}"][value="${CSS.escape(
              swatchValue
            )}"]`
          );
          if (radio) {
            radio.classList.add("disabled");
          }
        }
      });
    });
  }

  updateStockIndicators(
    container,
    stockData,
    variantStockEls,
    selectedOptions = []
  ) {
    if (!stockData) return;
    const optionsCount = stockData.options.length;

    variantStockEls.forEach((el) => {
      // Get the value - either from title attribute (regular options) or data-value (color swatches)
      let labelValue = el.getAttribute("title")?.trim();
      if (!labelValue) {
        labelValue = el.getAttribute("data-value")?.trim();
      }
      if (!labelValue) return;

      const fieldset = el.closest(".variant-option");
      if (!fieldset) return;

      const optionIndex = Array.from(fieldset.parentNode.children).indexOf(
        fieldset
      );

      // Remove old stock indicator
      el.querySelector(".variant-stock")?.remove();

      let stockLeft = 0;

      if (optionsCount === 1) {
        // Single option → sum directly
        stockLeft = stockData.variants
          .filter((v) => v.options[0] === labelValue)
          .reduce((acc, v) => acc + v.stock, 0);
      } else if (optionsCount >= 2) {
        if (optionIndex === 0) {
          // First-level (e.g., Size or Color)
          stockLeft = stockData.variants
            .filter((v) => v.options[0] === labelValue)
            .reduce((acc, v) => acc + v.stock, 0);
        } else if (optionIndex === 1 && selectedOptions[0]) {
          // Second-level (e.g., Age) → based on selected first option
          stockLeft = stockData.variants
            .filter(
              (v) =>
                v.options[0] === selectedOptions[0] &&
                v.options[1] === labelValue
            )
            .reduce((acc, v) => acc + v.stock, 0);
        }
      }

      if (stockLeft > 0 && stockLeft < 10) {
        const urgent = stockLeft < 5;
        // Check if it's a swatch (color option) - either has style with --swatch or is a quick-add-color-option
        const isSwatch =
          el.classList.contains("quick-add-color-option") ||
          (el.hasAttribute("style") &&
            el.getAttribute("style").includes("--swatch"));
        const div = document.createElement("div");
        div.className = `variant-stock ${
          isSwatch ? "variant-stock--swatch" : ""
        }`;
        div.dataset.stock = stockLeft;
        div.dataset.urgent = urgent ? "true" : "false";
        div.innerHTML = `
          <div class="dot ${urgent ? "urgent" : "warning"}"></div>
          ${stockLeft} left
        `;
        el.appendChild(div);
      }
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const variantInput = container.querySelector('input[name="id"]');
      if (!variantInput) return 9999;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      if (!variant) return 9999;

      const inventoryPolicy = variant.inventory_policy;
      const inventoryManagement = variant.inventory_management;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return variant.inventory_quantity || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    const getMinQuantity = () => {
      const minAttr = quantityInput.getAttribute("data-min");
      return minAttr ? parseInt(minAttr) : 1;
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const minValue = getMinQuantity();
      if (currentValue > minValue) {
        quantityInput.value = currentValue - 1;
        if (stockError) stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        return;
      }

      quantityInput.value = currentValue + 1;
      if (stockError) stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const maxQuantity = getMaxQuantity();
      const minValue = getMinQuantity();
      let newValue = parseInt(quantityInput.value) || minValue;

      if (newValue < minValue) newValue = minValue;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
      } else {
        if (stockError) stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitBtn = form.querySelector(".add-to-cart-btn");
      const originalText = submitBtn.textContent;
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const variantInput = form.querySelector('input[name="id"]');

      if (!variantInput) return;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
      const maxQuantity = variant?.inventory_quantity || 9999;
      const inventoryPolicy = variant?.inventory_policy;
      const inventoryManagement = variant?.inventory_management;

      // Validate stock
      if (
        inventoryManagement === "shopify" &&
        inventoryPolicy === "deny" &&
        quantity > maxQuantity
      ) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        if (quantityInput) quantityInput.value = maxQuantity;
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = window.translations?.cart?.adding || "Adding...";

      try {
        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: Math.min(quantity, maxQuantity),
              },
            ],
          }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = window.translations?.cart?.added || "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            product: this.currentProductData,
          });

          // Close modal after delay if it's open
          setTimeout(() => {
            if (this.modal && this.modal.classList.contains("open")) {
              this.close();
            }
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(".card-product__color-option");
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }
  });
});
